import React, { useEffect, useState } from 'react'
import { portal } from '../portalApi.js'
import { fmt } from '../api.js'

// 법인카드 지출결의 — 지출내역을 손으로 적는 대신 이미 들어와 있는 카드 사용내역에서 고른다.
// 고른 건은 결재가 올라가는 순간 '결재중'으로 잠기므로 두 번 기안될 일이 없다(db.create_document).

const day = (yyyymmdd) => {
  const s = String(yyyymmdd || '')
  return s.length >= 8 ? `${s.slice(0, 4)}.${s.slice(4, 6)}.${s.slice(6, 8)}` : s
}

export default function CardPicker({ cats = [], onChange }) {
  const [rows, setRows] = useState(null)
  const [sel, setSel] = useState(new Set())
  const [edit, setEdit] = useState({})     // id → 사람이 고쳐 넣은 계정과목·적요
  const [q, setQ] = useState('')
  const [err, setErr] = useState('')

  useEffect(() => {
    portal.get('/api/edoc/cards').then(setRows).catch((e) => setErr(e.message))
  }, [])

  // 선택·수정이 바뀔 때마다 기안에 올릴 지출내역을 다시 만든다
  useEffect(() => {
    if (!rows) return
    onChange(rows.filter((r) => sel.has(r.id)).map((r) => {
      const e = edit[r.id] || {}
      const memo = (e.detail ?? r.detail) || ''
      return {
        vendor: r.vendor,
        account_cat: e.account_cat ?? r.account_cat ?? '',
        detail: [day(r.trans_date), memo].filter(Boolean).join(' · '),
        bank: r.card_company,
        account: r.card_number,
        holder: r.approval_number,
        amount: r.amount,
        card_transaction_id: r.id,
      }
    }))
  }, [rows, sel, edit])

  if (err) return <div className="error-box">{err}</div>
  if (!rows) return <div className="muted">카드 사용내역을 불러오는 중…</div>

  const needle = q.trim()
  const shown = needle
    ? rows.filter((r) => `${r.vendor} ${r.card_number} ${r.detail} ${r.account_cat}`.includes(needle))
    : rows
  const picked = rows.filter((r) => sel.has(r.id))
  const total = picked.reduce((s, r) => s + (Number(r.amount) || 0), 0)
  const toggle = (id) => setSel((p) => {
    const n = new Set(p)
    n.has(id) ? n.delete(id) : n.add(id)
    return n
  })

  return (
    <>
      <div className="row" style={{ marginBottom: 10 }}>
        <input placeholder="가맹점·카드번호·적요 검색" style={{ width: 260 }}
          value={q} onChange={(e) => setQ(e.target.value)} />
        <span className="muted">기안 가능 {fmt(rows.length)}건</span>
        <span style={{ marginLeft: 'auto' }}>
          선택 <b>{picked.length}</b>건 · 총 사용액 <b>{fmt(total)}원</b>
        </span>
      </div>

      {rows.length === 0 && (
        <div className="hint">기안할 수 있는 카드 사용내역이 없습니다. 이미 결재가 올라갔거나, 내 팀에 배정된 카드가 없을 수 있습니다.</div>
      )}

      <div style={{ maxHeight: 420, overflowY: 'auto' }}>
        <table>
          <thead>
            <tr>
              <th style={{ width: 28 }}>
                <input type="checkbox"
                  checked={shown.length > 0 && shown.every((r) => sel.has(r.id))}
                  onChange={(e) => setSel(e.target.checked
                    ? new Set([...sel, ...shown.map((r) => r.id)])
                    : new Set([...sel].filter((id) => !shown.some((r) => r.id === id))))} />
              </th>
              <th>사용일</th><th className="l">가맹점</th><th className="l">카드사</th>
              <th className="l">카드번호</th><th className="l">계정과목</th><th className="l">적요</th><th>금액</th>
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 && rows.length > 0 && (
              <tr><td colSpan={8} className="c muted">검색 결과가 없습니다</td></tr>
            )}
            {shown.map((r) => {
              const e = edit[r.id] || {}
              const cat = e.account_cat ?? r.account_cat ?? ''
              return (
                <tr key={r.id} className={sel.has(r.id) ? 'hi' : ''}>
                  <td className="c"><input type="checkbox" checked={sel.has(r.id)} onChange={() => toggle(r.id)} /></td>
                  <td className="c">{day(r.trans_date)}</td>
                  <td className="l">{r.vendor}</td>
                  <td className="l">{r.card_company}</td>
                  <td className="l">{r.card_number}</td>
                  <td>
                    <select style={{ width: 130 }} value={cat}
                      onChange={(ev) => setEdit((p) => ({ ...p, [r.id]: { ...e, account_cat: ev.target.value } }))}>
                      <option value="">선택</option>
                      {cats.map((c) => <option key={c.code} value={`${c.code} ${c.name}`}>{c.name}</option>)}
                      {/* 카드내역에 이미 붙어 있던 표기가 목록에 없으면 그것도 살려 둔다 */}
                      {cat && !cats.some((c) => `${c.code} ${c.name}` === cat) && <option value={cat}>{cat}</option>}
                    </select>
                  </td>
                  <td>
                    <input style={{ width: 180, textAlign: 'left' }} value={e.detail ?? r.detail ?? ''}
                      onChange={(ev) => setEdit((p) => ({ ...p, [r.id]: { ...e, detail: ev.target.value } }))} />
                  </td>
                  <td>{fmt(r.amount)}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <div className="hint">
        카드에 붙여 둔 영수증은 기안할 때 문서 증빙으로 함께 넘어갑니다. 따로 올릴 것이 있으면 아래 증빙 첨부를 쓰세요.
      </div>
    </>
  )
}
